import React, { useState, useEffect, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import {
  format,
  addDays,
  addMonths,
  subMonths,
  startOfDay,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  isSameDay,
  isAfter,
  isBefore,
} from '../../../utils/date-fns-replacement';
import './custom-date-range-picker.styles.scss';

/**
 * Custom Date Range Picker Component
 * Replacement for react-date-range library
 * Uses custom date-fns replacement utility
 */
const CustomDateRangePicker = ({
  startDate,
  endDate,
  onDateChange,
  staticRanges = [],
  editableDateInputs = false,
  direction = "vertical",
  startDatePlaceholder = "From",
  endDatePlaceholder = "To",
  dateFormat = "DD/MM/YYYY",
  showDateDisplay = false,
  showPreview = true,
  showStaticRanges = false,
}) => {
  // Format date for input field - match react-date-range format (MMM dd, yyyy)
  const formatDateForInput = (date) => {
    if (!date) return '';
    // Match react-date-range default format: "Jan 22, 2026"
    return format(date, 'MMM dd, yyyy');
  };

  const [selectedRange, setSelectedRange] = useState({
    startDate: startDate || new Date(),
    endDate: endDate || new Date(),
    key: "selection",
  });

  const [currentMonth, setCurrentMonth] = useState(startDate || new Date());
  const [hoverDate, setHoverDate] = useState(null);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [inputDates, setInputDates] = useState({
    startDate: startDate ? formatDateForInput(startDate) : '',
    endDate: endDate ? formatDateForInput(endDate) : '',
  });
  const [tempInputValues, setTempInputValues] = useState({
    startDate: startDate ? formatDateForInput(startDate) : '',
    endDate: endDate ? formatDateForInput(endDate) : '',
  });

  // Parse input date string - handle "MMM dd, yyyy" format (react-date-range default)
  const parseInputDate = (dateStr) => {
    if (!dateStr) return null;
    
    // Try parsing as "MMM dd, yyyy" format (e.g., "Jan 22, 2026")
    const monthNamesShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const match = dateStr.match(/^(\w+)\s+(\d+),\s+(\d+)$/);
    if (match) {
      const [, monthStr, day, year] = match;
      const monthIndex = monthNamesShort.findIndex(m => m.toLowerCase() === monthStr.toLowerCase());
      if (monthIndex !== -1) {
        const date = new Date(parseInt(year, 10), monthIndex, parseInt(day, 10));
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    }
    
    // Fallback: try standard Date parsing
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date;
    }
    
    return null;
  };

  useEffect(() => {
    if (startDate || endDate) {
      setSelectedRange({
        startDate: startDate || selectedRange.startDate,
        endDate: endDate || selectedRange.endDate,
        key: "selection",
      });
      setInputDates({
        startDate: startDate ? formatDateForInput(startDate) : '',
        endDate: endDate ? formatDateForInput(endDate) : '',
      });
      setTempInputValues({
        startDate: startDate ? formatDateForInput(startDate) : '',
        endDate: endDate ? formatDateForInput(endDate) : '',
      });
    }
  }, [startDate, endDate]);

  // Close year picker when clicking outside
  useEffect(() => {
    if (!showYearPicker) return;

    const handleClickOutside = (event) => {
      const target = event.target;
      if (!target.closest('.year-picker-wrapper')) {
        setShowYearPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showYearPicker]);

  // Generate calendar days for current month
  const getCalendarDays = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);
    
    const days = [];
    let currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      days.push(new Date(currentDate));
      currentDate = addDays(currentDate, 1);
    }
    
    return days;
  };

  const calendarDays = useMemo(() => getCalendarDays(), [currentMonth]);

  // Handle date click
  const handleDateClick = (date) => {
    const dayStart = startOfDay(date);
    
    if (!selectedRange.startDate || (selectedRange.startDate && selectedRange.endDate)) {
      // Start new selection
      setSelectedRange({
        startDate: dayStart,
        endDate: null,
        key: "selection",
      });
    } else if (selectedRange.startDate && !selectedRange.endDate) {
      // Complete selection
      if (isBefore(dayStart, selectedRange.startDate)) {
        // Clicked date is before start date, swap them
        setSelectedRange({
          startDate: dayStart,
          endDate: selectedRange.startDate,
          key: "selection",
        });
      } else {
        setSelectedRange({
          ...selectedRange,
          endDate: dayStart,
        });
      }
    }
  };

  // Handle static range click
  const handleStaticRangeClick = (range) => {
    const rangeValue = range.range();
    const newRange = {
      startDate: rangeValue.startDate,
      endDate: rangeValue.endDate,
      key: "selection",
    };
    setSelectedRange(newRange);
    setInputDates({
      startDate: formatDateForInput(rangeValue.startDate),
      endDate: formatDateForInput(rangeValue.endDate),
    });
    setTempInputValues({
      startDate: formatDateForInput(rangeValue.startDate),
      endDate: formatDateForInput(rangeValue.endDate),
    });
    
    // Navigate calendar to show the start date of the selected range
    if (rangeValue.startDate) {
      setCurrentMonth(startOfMonth(rangeValue.startDate));
    }
    
    if (onDateChange) {
      onDateChange({ selection: newRange }, range.label);
    }
  };

  // Update range when selection changes
  useEffect(() => {
    if (selectedRange.startDate && selectedRange.endDate) {
      setInputDates({
        startDate: formatDateForInput(selectedRange.startDate),
        endDate: formatDateForInput(selectedRange.endDate),
      });
      setTempInputValues({
        startDate: formatDateForInput(selectedRange.startDate),
        endDate: formatDateForInput(selectedRange.endDate),
      });
      
      if (onDateChange) {
        let label = "Custom";
        staticRanges.forEach(range => {
          const rangeValue = range.range();
          if (isSameDay(selectedRange.startDate, rangeValue.startDate) &&
              isSameDay(selectedRange.endDate, rangeValue.endDate)) {
            label = range.label;
          }
        });
        onDateChange({ selection: selectedRange }, label);
      }
    }
  }, [selectedRange.startDate, selectedRange.endDate]);

  // Handle input change
  const handleInputChange = (e, field) => {
    const { value } = e.target;
    setTempInputValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle input update
  const handleDateUpdate = (field) => {
    let value = tempInputValues[field];
    
    if (value === inputDates[field]) {
      return;
    }

    const date = parseInputDate(value);
    
    if (date && !isNaN(date.getTime())) {
      const dayStart = startOfDay(date);
      
      if (field === 'startDate') {
        if (selectedRange.endDate && isAfter(dayStart, selectedRange.endDate)) {
          setTempInputValues(prev => ({
            ...prev,
            [field]: inputDates[field]
          }));
          return;
        }
        setSelectedRange({
          ...selectedRange,
          startDate: dayStart,
        });
      } else if (field === 'endDate') {
        if (selectedRange.startDate && isBefore(dayStart, selectedRange.startDate)) {
          setTempInputValues(prev => ({
            ...prev,
            [field]: inputDates[field]
          }));
          return;
        }
        setSelectedRange({
          ...selectedRange,
          endDate: dayStart,
        });
      }
    } else {
      setTempInputValues(prev => ({
        ...prev,
        [field]: inputDates[field]
      }));
    }
  };

  const handleKeyDown = (e, field) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      handleDateUpdate(field);
      if (e.key === 'Tab') {
        const nextInput = e.target.form?.elements[field === 'startDate' ? 1 : 0];
        nextInput?.focus();
      }
    }
  };

  // Check if date is in range
  const isDateInRange = (date) => {
    if (!selectedRange.startDate) return false;
    if (!selectedRange.endDate) {
      return false; // Don't show range until end date is selected
    }
    const dayStart = startOfDay(date);
    const start = startOfDay(selectedRange.startDate);
    const end = startOfDay(selectedRange.endDate);
    // Check if date is between start and end (inclusive)
    return (dayStart >= start && dayStart <= end) || (dayStart >= end && dayStart <= start);
  };

  // Check if date is start or end
  const isDateStart = (date) => {
    return selectedRange.startDate && isSameDay(date, selectedRange.startDate);
  };

  const isDateEnd = (date) => {
    return selectedRange.endDate && isSameDay(date, selectedRange.endDate);
  };

  // Check if date is in hover range (when selecting end date)
  const isDateInHoverRange = (date) => {
    if (!selectedRange.startDate || selectedRange.endDate || !hoverDate) return false;
    const dayStart = startOfDay(date);
    const start = startOfDay(selectedRange.startDate);
    const hoverDayStart = startOfDay(hoverDate);
    // Show preview range from start to hover date
    if (hoverDayStart >= start) {
      return dayStart >= start && dayStart <= hoverDayStart && !isSameDay(date, start);
    } else {
      return dayStart >= hoverDayStart && dayStart <= start && !isSameDay(date, start);
    }
  };

  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  // Handle year change
  const handleYearChange = (year) => {
    const newDate = new Date(currentMonth);
    newDate.setFullYear(year);
    setCurrentMonth(newDate);
    setShowYearPicker(false);
  };

  // Generate year options (current year ± 10 years)
  const getYearOptions = () => {
    const currentYear = currentMonth.getFullYear();
    const years = [];
    for (let i = currentYear - 10; i <= currentYear + 10; i++) {
      years.push(i);
    }
    return years;
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className={`custom-date-range-picker ${direction}`} style={{ width: showStaticRanges && staticRanges.length > 0 ? '330px' : '220px' }}>
            <div className="date-range-picker-container" style={{ width: showStaticRanges && staticRanges.length > 0 ? '330px' : '220px' }}>
        {showStaticRanges && staticRanges.length > 0 && (
          <div className="static-ranges">
            {staticRanges.map((range, index) => (
              <div
                key={index}
                className="static-range-item"
                onClick={() => handleStaticRangeClick(range)}
              >
                {range.label}
              </div>
            ))}
          </div>
        )}

<div className="calendar-wrapper">
{editableDateInputs && (
        <div className="date-inputs">
          <Form.Group>
            <Form.Control
              type="text"
              placeholder={startDatePlaceholder}
              value={tempInputValues.startDate}
              onChange={(e) => handleInputChange(e, 'startDate')}
              onBlur={() => handleDateUpdate('startDate')}
              onKeyDown={(e) => handleKeyDown(e, 'startDate')}
            />
          </Form.Group>
          <Form.Group>
            <Form.Control
              type="text"
              placeholder={endDatePlaceholder}
              value={tempInputValues.endDate}
              onChange={(e) => handleInputChange(e, 'endDate')}
              onBlur={() => handleDateUpdate('endDate')}
              onKeyDown={(e) => handleKeyDown(e, 'endDate')}
            />
          </Form.Group>
        </div>
      )}

        <div className="calendar-container">
          <div className="calendar-header">
            <button
              type="button"
              className="nav-button"
              onClick={goToPreviousMonth}
            >
              ‹
            </button>
            <div className="month-year-separate">
              <span className="month-name">{monthNames[currentMonth.getMonth()]}</span>
              <div className="year-picker-wrapper">
                <button
                  type="button"
                  className="year-picker-button"
                  onClick={() => setShowYearPicker(!showYearPicker)}
                >
                  {currentMonth.getFullYear()}
                </button>
                {showYearPicker && (
                  <div className="year-picker-dropdown">
                    {getYearOptions().map(year => (
                      <div
                        key={year}
                        className={`year-option ${year === currentMonth.getFullYear() ? 'selected' : ''}`}
                        onClick={() => handleYearChange(year)}
                      >
                        {year}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              className="nav-button"
              onClick={goToNextMonth}
            >
              ›
            </button>
          </div>

          <div className="calendar-weekdays">
            {weekDays.map(day => (
              <div key={day} className="weekday">{day}</div>
            ))}
          </div>

          <div className="calendar-days">
            {calendarDays.map((date, index) => {
              const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
              const isInRange = isDateInRange(date);
              const isStart = isDateStart(date);
              const isEnd = isDateEnd(date);
              const isInHoverRange = isDateInHoverRange(date);
              const isToday = isSameDay(date, new Date());

              return (
                <div
                  key={index}
                  className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${
                    isInRange || isInHoverRange ? 'in-range' : ''
                  } ${isStart ? 'start-date' : ''} ${isEnd ? 'end-date' : ''} ${
                    isToday ? 'today' : ''
                  }`}
                  onClick={() => handleDateClick(date)}
                  onMouseEnter={() => {
                    if (selectedRange.startDate && !selectedRange.endDate) {
                      setHoverDate(date);
                    }
                  }}
                  onMouseLeave={() => setHoverDate(null)}
                >
                  {format(date, 'd')}
                </div>
              );
            })}
          </div>

        </div>
</div>
      </div>
    </div>
  );
};

/**
 * Helper function to create static ranges (replacement for createStaticRanges)
 */
export const createStaticRanges = (ranges) => {
  return ranges.map(range => ({
    label: range.label,
    range: range.range,
  }));
};

export default CustomDateRangePicker;
